/**
 * TransactionTable — Umsätze wie sevdesk „Transaktionen“: Tabelle ab lg, darunter Karten.
 * Drei-Punkte-Menü je Zeile: Zuordnen, Kredit, Privatentnahme, Geldbewegung, Gebühren, Löschen
 * (gebucht: Zuordnung aufheben · gelöscht: Wiederherstellen).
 */
import React from 'react';
import { ArrowLeftRight, Banknote, Landmark, Link2, MoreHorizontal, Receipt, RotateCcw, Trash2, Undo2, User } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { fmtEUR } from '@/lib/buchhaltungApi';
import { StatusBadge } from '@/components/ausgaben/ExpensesTable';
import { BADGE, TH } from '@/components/ausgaben/dna';
import { txStatus, txRest, TX_STATUS_LABEL } from '@/lib/reconcileLogic';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');
const STATUS_BADGE = { offen: BADGE.open, teilweise: BADGE.partial, gebucht: BADGE.paid, ignoriert: BADGE.neutral, geloescht: BADGE.neutral };
const CAT_ICON = { kredit: Landmark, gesellschafter: User, transit: ArrowLeftRight, gebuehren: Banknote };

function TxBadge({ tx }) {
  const s = txStatus(tx);
  return <StatusBadge label={TX_STATUS_LABEL[s]} className={STATUS_BADGE[s]} />;
}

function RowMenu({ tx, actions }) {
  const s = txStatus(tx);
  const openable = s === 'offen' || s === 'teilweise';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label="Aktionen" onClick={(e) => e.stopPropagation()}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-dna-muted hover:bg-dna-warm hover:text-dna-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dna-focus">
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 rounded-lg border-dna-line p-1" onClick={(e) => e.stopPropagation()}>
        {openable && (<>
          <DropdownMenuItem className="h-10 gap-2 text-dna-primary" onClick={() => actions.onOpen(tx, 'items')}>
            <Receipt className="h-4 w-4" /> {Number(tx.amount) >= 0 ? 'Rechnung zuordnen' : 'Beleg zuordnen'}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {[
            ['kredit', 'Rückzahlung eines Kredites'],
            ['gesellschafter', Number(tx.amount) >= 0 ? 'Einlage Gesellschafter' : 'Privatentnahme'],
            ['transit', 'Geldbewegung (z. B. PayPal → Bank)'],
            ['gebuehren', 'Gebühren für Zahlungsverkehr'],
          ].map(([key, label]) => {
            const Icon = CAT_ICON[key];
            return (
              <DropdownMenuItem key={key} className="h-10 gap-2 text-dna-primary" onClick={() => actions.onOpen(tx, 'account', key)}>
                <Icon className="h-4 w-4" /> {label}
              </DropdownMenuItem>
            );
          })}
          {s === 'offen' && (<>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="h-10 gap-2 text-dna-due-fg" onClick={() => actions.onDelete(tx)}><Trash2 className="h-4 w-4" /> Löschen</DropdownMenuItem>
          </>)}
        </>)}
        {(s === 'gebucht' || s === 'teilweise' || s === 'ignoriert') && (
          <DropdownMenuItem className="h-10 gap-2 text-dna-primary" onClick={() => actions.onUndo(tx)}><Undo2 className="h-4 w-4" /> Zuordnung aufheben</DropdownMenuItem>
        )}
        {s === 'geloescht' && (
          <DropdownMenuItem className="h-10 gap-2 text-dna-primary" onClick={() => actions.onRestore(tx)}><RotateCcw className="h-4 w-4" /> Wiederherstellen</DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Amount({ tx }) {
  const s = txStatus(tx);
  return (
    <span className="block text-right">
      <span className={cn('block text-[14px] font-semibold tabular-nums', Number(tx.amount) >= 0 ? 'text-dna-paid-fg' : 'text-dna-text')}>{fmtEUR(tx.amount)}</span>
      {s === 'teilweise' && <span className="block text-[12px] tabular-nums text-dna-muted">offen {fmtEUR(txRest(tx))}</span>}
      {Number(tx.fees) > 0 && <span className="block text-[12px] tabular-nums text-dna-muted">Gebühr {fmtEUR(tx.fees)}</span>}
    </span>
  );
}

function Suggestion({ suggestion, tx, actions }) {
  if (!suggestion) return null;
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); actions.onOpen(tx, 'items', null, suggestion.item); }}
      className="inline-flex max-w-full items-center gap-1 rounded-full bg-dna-draft-bg px-2 py-0.5 text-[12px] font-semibold text-dna-draft-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dna-focus">
      <Link2 className="h-3 w-3 shrink-0" aria-hidden="true" />
      <span className="truncate">Vorschlag <span className="font-mono">{suggestion.item.invoice_no}</span></span>
    </button>
  );
}

/**
 * @param {{ rows: object[], suggestions: Map<number, object>, showAccount: boolean,
 *   actions: { onOpen, onDelete, onUndo, onRestore } }} props
 */
export default function TransactionTable({ rows, suggestions, showAccount, actions }) {
  const clickable = (tx) => ['offen', 'teilweise'].includes(txStatus(tx));
  return (
    <>
      <table className="hidden w-full lg:table">
        <thead className="border-b border-dna-line">
          <tr>
            <th className={cn(TH, 'w-32')}>Status</th>
            <th className={cn(TH, 'w-28')}>Datum</th>
            <th className={TH}>Auftraggeber / Empfänger · Verwendungszweck</th>
            {showAccount && <th className={cn(TH, 'w-40')}>Konto</th>}
            <th className={cn(TH, 'w-36 text-right')}>Betrag</th>
            <th className="w-14" aria-label="Aktionen" />
          </tr>
        </thead>
        <tbody>
          {rows.map((tx) => (
            <tr key={tx.id} onClick={() => clickable(tx) && actions.onOpen(tx, 'items')}
              className={cn('border-b border-dna-line last:border-0', clickable(tx) && 'cursor-pointer hover:bg-dna-warm')}>
              <td className="px-3 py-3 align-top"><TxBadge tx={tx} /></td>
              <td className="px-3 py-3 align-top text-[14px] tabular-nums text-dna-text">{fmtDate(tx.booking_date)}</td>
              <td className="max-w-0 px-3 py-3 align-top">
                <p className="truncate text-[14px] font-semibold text-dna-text">{tx.counterparty_name || '—'}</p>
                <p className="truncate text-[13px] text-dna-muted">{tx.purpose || 'kein Verwendungszweck'}</p>
                {tx.delete_reason && <p className="truncate text-[12px] text-dna-muted">Gelöscht: {tx.delete_reason}</p>}
                <div className="mt-1"><Suggestion suggestion={suggestions.get(tx.id)} tx={tx} actions={actions} /></div>
              </td>
              {showAccount && <td className="max-w-0 px-3 py-3 align-top"><span className="block truncate text-[13px] text-dna-muted" title={tx.fibu_financial_connections?.display_name}>{tx.fibu_financial_connections?.display_name}</span></td>}
              <td className="px-3 py-3 align-top"><Amount tx={tx} /></td>
              <td className="px-1 py-1.5 align-top"><RowMenu tx={tx} actions={actions} /></td>
            </tr>
          ))}
        </tbody>
      </table>

      <ul className="divide-y divide-dna-line lg:hidden">
        {rows.map((tx) => (
          <li key={tx.id} className="flex gap-2 py-3">
            <div className="min-w-0 flex-1 space-y-1">
            <button type="button" disabled={!clickable(tx)} onClick={() => actions.onOpen(tx, 'items')}
              className="block w-full space-y-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dna-focus">
              <span className="flex items-start justify-between gap-2">
                <span className="truncate text-[14px] font-semibold text-dna-text">{tx.counterparty_name || '—'}</span>
                <Amount tx={tx} />
              </span>
              <span className="block truncate text-[13px] text-dna-muted">{tx.purpose || 'kein Verwendungszweck'}</span>
              <span className="flex flex-wrap items-center gap-2 text-[12px] text-dna-muted">
                <TxBadge tx={tx} /> {fmtDate(tx.booking_date)}{showAccount && ` · ${tx.fibu_financial_connections?.display_name || ''}`}
              </span>
            </button>
            <Suggestion suggestion={suggestions.get(tx.id)} tx={tx} actions={actions} />
            </div>
            <RowMenu tx={tx} actions={actions} />
          </li>
        ))}
      </ul>
    </>
  );
}
